import { useEffect, useState } from 'react';
import { Spinner } from '../components';
import { captureException } from '../lib/sentry';
import { useRouter } from 'next/router';
import { FormattedMessage } from 'react-intl';
import { globalMessages } from '../lib/messages';
import { useStripe } from '../lib/useStripe';
import {
  useAsyncProcessCompletion,
  PostMessagePaymentState
} from './AsyncProcessCompletion';
import { PaymentDocumentType } from '../core-types';

export const StripeConfirmCardPayment = ({
  clientSecret,
  paymentMethod,
  token,
  type,
  publishableKey,
  stripeScriptTimeout = 3000,
  showSpinner = true,
  onError,
  onAsyncProcessCompleted
}: {
  clientSecret: string | null;
  paymentMethod: string | null;
  token: string;
  type: PaymentDocumentType;
  publishableKey: string;
  stripeScriptTimeout?: number;
  showSpinner?: boolean;
  onError?: () => void;
  onAsyncProcessCompleted?: (state: PostMessagePaymentState) => void;
}) => {
  const [cardConfirmed, setCardConfirmed] = useState(false);
  const [paymentError, setPaymentError] = useState(false);
  const { query, pathname } = useRouter();

  const { stripe, error: stripeError, loadStripeJs } = useStripe({
    publishableKey,
    timeout: stripeScriptTimeout
  });

  useAsyncProcessCompletion({
    skip: !cardConfirmed,
    token,
    type,
    paymentState: 'success',
    onAsyncProcessCompleted
  });

  useEffect(() => {
    if (!clientSecret || !paymentMethod || !stripe) {
      return;
    }
    try {
      stripe
        .confirmCardPayment(clientSecret, {
          payment_method: paymentMethod
        })
        .then(({ error }) => {
          if (error) {
            if (process.env.NODE_ENV !== 'production') {
              // eslint-disable-next-line no-console
              console.error('stripe.confirmCardPayment error', error);
            }
            onError && onError();
            setPaymentError(true);
            setCardConfirmed(false);
          } else {
            setPaymentError(false);
            setCardConfirmed(true);
          }
        })
        .catch(err => {
          onError && onError();
          captureException(err, { query, pathname });
          setPaymentError(true);
        });
    } catch {
      onError && onError();
    }
  }, [clientSecret, stripe, paymentMethod, pathname, query]);

  return (
    <>
      {loadStripeJs}
      {paymentError && <FormattedMessage {...globalMessages.genericError} />}
      {stripeError}
      {!paymentError && !stripeError && showSpinner && (
        <div aria-label="Loading">
          <Spinner size={48} />
        </div>
      )}
    </>
  );
};
